<script setup>
import { ref, nextTick, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import EmojiPicker from 'dashboard/components-next/emoji/EmojiPicker.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

const props = defineProps({
  pendingFaq: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();

const showDrawer = ref(false);
const isSubmitting = ref(false);

// 表单数据
const question = ref('');
const answers = ref([]);
const similarQuestions = ref([]);
const selectedKnowledgeBaseId = ref(null);

// 知识库列表
const knowledgeBases = useMapGetter('pilotKnowledgeBases/getRecords');
const knowledgeBasesArray = computed(() => knowledgeBases.value || []);
const knowledgeBaseOptions = computed(() =>
  knowledgeBasesArray.value.map(kb => ({
    label: kb.name,
    value: kb.id,
  }))
);

// 表情选择器
const openEmojiPickerId = ref(null);
const inputRefs = ref({});

// Markdown 渲染
const renderMarkdown = content => {
  if (!content) return '';
  const formatter = new MessageFormatter(content);
  return formatter.formattedMessage;
};

import logoUrl from 'assets/images/dashboard/pilot/logo.png';

// 初始化表单数据
const initializeForm = () => {
  if (props.pendingFaq) {
    question.value = props.pendingFaq.question || '';
    answers.value = [
      { id: Date.now(), content: props.pendingFaq.answer || '', error: false },
    ];
    similarQuestions.value = [];
    selectedKnowledgeBaseId.value = null;
  }
};

// 切换表情选择器
const toggleEmojiPicker = answerId => {
  if (openEmojiPickerId.value === answerId) {
    openEmojiPickerId.value = null;
  } else {
    openEmojiPickerId.value = answerId;
  }
};

// 选择表情
const handleSelectEmoji = (emoji, answerId) => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    answer.content += emoji;
  }
  openEmojiPickerId.value = null;
};

// 添加答案
const addAnswer = () => {
  const newId = Date.now();
  answers.value.push({
    id: newId,
    content: '',
    error: false,
  });
  nextTick(() => {
    const editorElement = document.querySelector(
      `[data-answer-id="${newId}"] .ProseMirror`
    );
    if (editorElement) {
      editorElement.focus();
    }
  });
};

// 删除答案
const removeAnswer = id => {
  if (answers.value.length <= 1) {
    useAlert('至少需要保留一个答案');
    return;
  }
  answers.value = answers.value.filter(a => a.id !== id);
};

// 处理回答编辑器失去焦点
const handleAnswerBlur = answerId => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    answer.error = !answer.content || !answer.content.trim();
  }
};

// 清除回答错误状态
const clearAnswerError = answerId => {
  const answer = answers.value.find(a => a.id === answerId);
  if (answer) {
    answer.error = false;
  }
};

// 添加相似问题
const addSimilarQuestion = () => {
  const newId = Date.now();
  similarQuestions.value.unshift({
    id: newId,
    question: '',
    isEditing: true,
    error: false,
  });
  nextTick(() => {
    const input = inputRefs.value[newId];
    if (input) {
      input.focus();
    }
  });
};

// 删除相似问题
const removeSimilarQuestion = id => {
  const index = similarQuestions.value.findIndex(q => q.id === id);
  if (index !== -1) {
    similarQuestions.value.splice(index, 1);
  }
};

// 编辑相似问题
const editSimilarQuestion = id => {
  const questionItem = similarQuestions.value.find(q => q.id === id);
  if (questionItem) {
    questionItem.isEditing = true;
    questionItem.error = false;
    nextTick(() => {
      const input = inputRefs.value[id];
      if (input) {
        input.focus();
      }
    });
  }
};

// 保存相似问题
const saveSimilarQuestion = id => {
  const questionItem = similarQuestions.value.find(q => q.id === id);
  if (!questionItem) return;

  if (!questionItem.question.trim()) {
    questionItem.error = true;
    questionItem.isEditing = true;
    return;
  }
  questionItem.error = false;
  questionItem.isEditing = false;
};

const setInputRef = (id, el) => {
  if (el) {
    inputRefs.value[id] = el;
  }
};

// 相似问题假数据示例
const exampleSimilarQuestions = [
  { question: '这个问题怎么理解？' },
  { question: '能详细解释一下吗？' },
  { question: '可以举个例子说明吗？' },
];

// 打开抽屉
const openDrawer = () => {
  // 确保 props 已更新后再初始化表单
  nextTick(() => {
    initializeForm();
  });
  showDrawer.value = true;
};

// 关闭抽屉
const closeDrawer = () => {
  showDrawer.value = false;
  emit('close');
};

// 提交创建
const handleSubmit = async (publish = false) => {
  if (!selectedKnowledgeBaseId.value) {
    useAlert('请选择目标知识库');
    return;
  }

  if (!question.value.trim()) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.QUESTION_REQUIRED'));
    return;
  }

  // 验证所有答案是否已填写
  let hasEmptyAnswer = false;
  answers.value.forEach(answer => {
    if (!answer.content || !answer.content.trim()) {
      answer.error = true;
      hasEmptyAnswer = true;
    }
  });

  if (hasEmptyAnswer) {
    useAlert('请完善回答');
    return;
  }

  const validAnswers = answers.value.filter(a => a.content && a.content.trim());
  if (validAnswers.length === 0) {
    useAlert('至少需要填写一个答案');
    return;
  }

  // 验证相似问题
  if (similarQuestions.value.length > 0) {
    const hasEmptyQuestion = similarQuestions.value.some(
      sq => !sq.question.trim()
    );
    if (hasEmptyQuestion) {
      useAlert('请完善相似问题');
      return;
    }
  }

  isSubmitting.value = true;

  try {
    // 1. 创建 FAQ
    const faqData = {
      knowledge_base_id: selectedKnowledgeBaseId.value,
      question: question.value,
      answers: validAnswers.map(a => a.content.trim()),
      similar_questions: similarQuestions.value
        .filter(sq => sq.question.trim())
        .map(sq => sq.question.trim()),
      publish: publish,
    };

    const createdFaq = await store.dispatch('pilotFaqs/create', faqData);

    // 2. 标记 pending FAQ 为已通过，并传递 faq_id
    await store.dispatch('pilotPendingFaqs/approve', {
      id: props.pendingFaq.id,
      knowledgeBaseId: selectedKnowledgeBaseId.value,
      faqId: createdFaq.id,
    });

    useAlert(
      publish
        ? t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_AND_PUBLISH_SUCCESS')
        : t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_SUCCESS')
    );

    emit('created');
    closeDrawer();
  } catch (error) {
    console.error('Failed to create FAQ:', error);
    const errorMessage =
      error?.response?.data?.message ||
      t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// 暴露方法
defineExpose({
  open: openDrawer,
  close: closeDrawer,
});

// 监听 pendingFaq 变化，抽屉打开时重新初始化表单
watch(
  () => props.pendingFaq,
  newVal => {
    if (newVal && showDrawer.value) {
      initializeForm();
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <!-- 遮罩层 -->
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showDrawer && pendingFaq"
        class="fixed inset-0 bg-black/50 z-[9998]"
        @click="closeDrawer"
      />
    </transition>

    <!-- 抽屉面板 -->
    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="showDrawer && pendingFaq"
        class="fixed left-0 top-0 h-full w-[50rem] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
      >
        <!-- Loading Overlay -->
        <div
          v-if="isSubmitting"
          class="absolute inset-0 z-50 flex items-center justify-center bg-n-alpha-3 backdrop-blur-sm"
        >
          <div class="flex flex-col items-center gap-3">
            <Spinner />
            <p class="text-sm text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.CREATING') }}
            </p>
          </div>
        </div>

        <!-- 头部 -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700 shrink-0"
        >
          <h3 class="text-lg font-medium text-n-slate-12">
            审核并创建标准问答（FAQ）到知识库
          </h3>
          <button
            class="p-1 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors"
            @click="closeDrawer"
          >
            <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="flex flex-col gap-6">
            <!-- 目标知识库 -->
            <div>
              <h2 class="text-n-slate-12 text-base font-medium mb-4">
                保存到知识库
              </h2>
              <div class="flex flex-col gap-1">
                <label class="text-n-slate-12 text-sm font-medium">
                  {{ $t('PILOT.PENDING_FAQS.CREATE_FAQ.SELECT_KB') }}
                  <span class="text-n-brand-500">*</span>
                </label>
                <ComboBox
                  v-model="selectedKnowledgeBaseId"
                  :options="knowledgeBaseOptions"
                  :placeholder="
                    $t('PILOT.PENDING_FAQS.APPROVE.SELECT_KB_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <!-- 问题区域 -->
            <div>
              <h2 class="text-n-slate-12 text-base font-medium mb-4">问题</h2>

              <!-- 标准问题 -->
              <div class="flex flex-col gap-1 mb-5">
                <label class="text-n-slate-12 text-sm font-medium">
                  {{ $t('PILOT.PENDING_FAQS.CREATE_FAQ.QUESTION') }}
                  <span class="text-n-brand-500">*</span>
                </label>
                <input
                  v-model="question"
                  type="text"
                  :placeholder="
                    $t('PILOT.KNOWLEDGE_BASES.FAQS.QUESTION_PLACEHOLDER')
                  "
                  class="w-full px-4 py-3 bg-n-slate-white dark:bg-n-slate-800 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-n-brand-500"
                />
              </div>

              <!-- 相似问题 -->
              <div>
                <div class="mb-3">
                  <label class="text-n-slate-12 text-sm font-medium mb-2 block">
                    {{ $t('PILOT.PENDING_FAQS.CREATE_FAQ.SIMILAR_QUESTIONS') }}
                  </label>
                  <p class="text-n-slate-11 text-sm">
                    作为标准问题的补充，衍生相同意思的问题，提高用户问题的命中率。
                  </p>
                </div>

                <!-- 假数据引导区（没有相似问题时显示） -->
                <div
                  v-if="similarQuestions.length === 0"
                  class="relative w-full"
                >
                  <!-- 假数据列表（带透明度） -->
                  <div class="w-full space-y-3 opacity-50 pointer-events-none">
                    <div
                      v-for="(sq, index) in exampleSimilarQuestions.slice(0, 3)"
                      :key="`example-sq-${index}`"
                      class="flex items-start gap-4 p-4 bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-weak"
                    >
                      <!-- 图标 -->
                      <div
                        class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
                      >
                        <Icon icon="i-lucide-help-circle" class="h-5 w-5" />
                      </div>
                      <!-- 问题文本 -->
                      <span
                        class="text-sm text-n-slate-700 dark:text-n-slate-300 flex-1"
                      >
                        {{ sq.question }}
                      </span>
                    </div>
                  </div>
                  <!-- 渐变蒙层 + 内容 -->
                  <div
                    class="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end w-full bg-gradient-to-t from-n-solid-2 from-25% to-transparent pt-24 pb-6"
                  >
                    <div
                      class="flex flex-col items-center justify-center gap-6"
                    >
                      <div
                        class="flex flex-col items-center justify-center gap-3"
                      >
                        <h2
                          class="text-xl font-medium text-center text-n-slate-12"
                        >
                          添加相似问题
                        </h2>
                        <p class="max-w-xl text-sm text-center text-n-slate-11">
                          作为标准问题的补充，提高用户问题的命中率
                        </p>
                      </div>
                      <Button
                        :label="
                          $t('PILOT.KNOWLEDGE_BASES.FAQS.ADD_SIMILAR_QUESTION')
                        "
                        icon="i-lucide-plus"
                        blue
                        @click="addSimilarQuestion"
                      />
                    </div>
                  </div>
                </div>

                <!-- 有相似问题时显示真实列表 -->
                <div v-else class="space-y-2">
                  <!-- 已添加的相似问题 -->
                  <div
                    v-for="sq in similarQuestions"
                    :key="sq.id"
                    class="w-full flex items-center gap-3 p-4 bg-n-slate-white dark:bg-n-slate-800 border border-n-weak rounded-lg"
                  >
                    <!-- 显示模式 -->
                    <template v-if="!sq.isEditing">
                      <div
                        class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
                      >
                        <Icon icon="i-lucide-help-circle" class="h-5 w-5" />
                      </div>
                      <span
                        class="flex-1 text-sm text-n-slate-700 dark:text-n-slate-300 cursor-pointer"
                        @click="editSimilarQuestion(sq.id)"
                      >
                        {{ sq.question }}
                      </span>
                      <div class="flex items-center gap-2 shrink-0">
                        <Button
                          icon="i-lucide-pencil"
                          size="xs"
                          color="slate"
                          variant="faded"
                          @click="editSimilarQuestion(sq.id)"
                        />
                        <Button
                          icon="i-lucide-trash-2"
                          size="xs"
                          color="ruby"
                          variant="faded"
                          @click="removeSimilarQuestion(sq.id)"
                        />
                      </div>
                    </template>

                    <!-- 编辑模式 -->
                    <template v-else>
                      <div class="w-full">
                        <div class="flex items-center gap-3">
                          <div class="flex-1">
                            <Input
                              :ref="el => setInputRef(sq.id, el)"
                              v-model="sq.question"
                              :placeholder="
                                $t(
                                  'PILOT.KNOWLEDGE_BASES.FAQS.SIMILAR_QUESTION_PLACEHOLDER'
                                )
                              "
                              :message-type="sq.error ? 'error' : 'info'"
                              size="sm"
                              maxlength="200"
                              @blur="saveSimilarQuestion(sq.id)"
                              @keyup.enter="saveSimilarQuestion(sq.id)"
                            />
                          </div>
                          <div class="flex items-center gap-2 shrink-0">
                            <Button
                              icon="i-lucide-check"
                              size="xs"
                              color="slate"
                              variant="faded"
                              @click="saveSimilarQuestion(sq.id)"
                            />
                            <Button
                              icon="i-lucide-trash-2"
                              size="xs"
                              color="ruby"
                              variant="faded"
                              @click="removeSimilarQuestion(sq.id)"
                            />
                          </div>
                        </div>
                        <p class="text-xs text-n-slate-11 mt-1 text-right">
                          {{ sq.question?.length || 0 }}/200
                        </p>
                      </div>
                    </template>
                  </div>
                  <!-- 添加更多相似问题按钮 -->
                  <button
                    v-if="similarQuestions.length < 3"
                    type="button"
                    class="w-full text-n-brand hover:bg-n-alpha-2 text-sm font-medium transition-colors duration-200 rounded-lg px-4 py-3 inline-flex items-center justify-center gap-1.5 border border-dashed border-n-brand/30"
                    @click="addSimilarQuestion"
                  >
                    <Icon icon="i-lucide-plus" class="h-4 w-4 shrink-0" />
                    {{ $t('PILOT.KNOWLEDGE_BASES.FAQS.ADD_SIMILAR_QUESTION') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- 回答区域 -->
            <div>
              <div class="mb-4">
                <h2 class="text-n-slate-12 text-base font-medium mb-2">
                  答案 <span class="text-n-brand-500">*</span>
                </h2>
                <p class="text-n-slate-11 text-sm">
                  {{ $t('PILOT.PENDING_FAQS.CREATE_FAQ.ANSWERS_HINT') }}
                </p>
              </div>

              <div class="space-y-4">
                <div
                  v-for="answer in answers"
                  :key="answer.id"
                  class="border border-n-weak rounded-lg overflow-hidden"
                >
                  <!-- 工具栏 -->
                  <div class="flex items-center justify-between px-3 py-2">
                    <div class="flex items-center gap-2 relative">
                      <button
                        type="button"
                        class="p-1.5 text-n-slate-7 hover:text-n-slate-9 rounded transition-colors"
                        @click="toggleEmojiPicker(answer.id)"
                      >
                        <Icon icon="i-lucide-smile" class="h-4 w-4" />
                      </button>
                      <EmojiPicker
                        :is-open="openEmojiPickerId === answer.id"
                        @select-emoji="
                          emoji => handleSelectEmoji(emoji, answer.id)
                        "
                        @close="openEmojiPickerId = null"
                      />
                    </div>
                    <button
                      v-if="answers.length > 1"
                      class="p-1.5 text-n-slate-7 hover:text-n-ruby-9 rounded transition-colors"
                      @click="removeAnswer(answer.id)"
                    >
                      <Icon icon="i-lucide-trash-2" class="h-4 w-4" />
                    </button>
                  </div>

                  <!-- 编辑器 + 预览 (70% + 30%) -->
                  <div
                    class="flex gap-3 px-3 pb-3 items-stretch"
                    :data-answer-id="answer.id"
                  >
                    <!-- 左侧编辑器 70% -->
                    <div class="w-[70%] min-h-[160px]">
                      <Editor
                        v-model="answer.content"
                        placeholder="编辑机器人回答"
                        :max-length="800"
                        :show-character-count="true"
                        :focus-on-mount="false"
                        :message-type="answer.error ? 'error' : 'info'"
                        class="border-0 rounded-none h-full"
                        @blur="handleAnswerBlur(answer.id)"
                        @focus="clearAnswerError(answer.id)"
                      />
                    </div>

                    <!-- 右侧预览 30% -->
                    <div class="w-[30%] flex-shrink-0">
                      <div class="flex items-end gap-2 h-full">
                        <!-- 机器人头像 -->
                        <div class="flex-shrink-0">
                          <img
                            :src="logoUrl"
                            alt="机器人"
                            class="w-11 h-11 rounded-full object-contain"
                          />
                        </div>
                        <!-- 预览气泡 -->
                        <div class="flex-1">
                          <div
                            class="bg-n-solid-iris text-n-slate-12 p-3 rounded-2xl rounded-bl-none shadow-lg"
                          >
                            <div
                              v-if="answer.content"
                              v-dompurify-html="renderMarkdown(answer.content)"
                              class="prose prose-bubble"
                            />
                            <div
                              v-else
                              class="text-sm text-n-slate-11 text-center"
                            >
                              回答预览
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 添加答案按钮 -->
                <button
                  type="button"
                  class="text-n-brand hover:bg-n-alpha-2 text-sm font-medium transition-colors duration-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
                  @click="addAnswer"
                >
                  <Icon icon="i-lucide-plus" class="h-4 w-4 shrink-0" />
                  <span>添加回答</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div
          class="flex items-center gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700 shrink-0"
        >
          <Button
            :label="$t('PILOT.KNOWLEDGE_BASES.FAQS.CREATE_AND_PUBLISH')"
            icon="i-lucide-upload-cloud"
            class="flex-1"
            :disabled="isSubmitting"
            @click="handleSubmit(true)"
          />
          <Button
            variant="faded"
            color="slate"
            :label="$t('PILOT.PENDING_FAQS.CREATE_FAQ.CANCEL')"
            class="flex-1"
            :disabled="isSubmitting"
            @click="closeDrawer"
          />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
:deep(.ProseMirror-woot-style) {
  min-height: 140px;
  max-height: none !important;
  height: auto;
}

:deep(.ProseMirror-menubar-wrapper) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.ProseMirror-menubar-wrapper .ProseMirror) {
  flex: 1;
  min-height: 140px;
  max-height: none !important;
}
</style>
